/**
 * Los templates de WhatsApp vistos desde el panel (change `conversaciones-templates`, Fase 2), en
 * puro: las reglas de Meta para avisar mientras se escribe —las mismas del conversation-service—, el
 * preview, qué se puede mandar, y cómo se nombran el estado y la categoría. Nada de esto decide algo
 * que el backend no vuelva a verificar.
 */
import type {
  TemplateDefinition,
  TemplateItem,
  TemplatePublication,
  TemplateStatus,
  TemplateVariable,
  TemplatesView,
} from "@/services/conversations.api";

export const LARGO_MAXIMO_DEL_NOMBRE = 100;
export const LARGO_MAXIMO_DEL_BODY = 1024;
export const LARGO_MAXIMO_DEL_FOOTER = 60;
export const PALABRAS_FIJAS_POR_VARIABLE = 3;
/** Nuestro marcador, con una sola llave: se reemplaza al publicar por el nombre del número en WhatsApp. */
export const MARCADOR_DE_TIENDA = "{tienda}";
/** Donde Meta publica lo que cobra por categoría y país. No hay API: por eso el panel no muestra precio. */
export const RATE_CARD_URL = "https://business.whatsapp.com/products/platform-pricing";

const NOMBRE = /^[a-z0-9_]+$/;
const VARIABLE = /\{\{(\d+)\}\}/g;

export interface TextoDeTemplate {
  name: string;
  body: string;
  footer?: string | null;
  examples: string[];
  variables?: TemplateVariable[];
}

/** Los números de las variables del texto, en el orden en que aparecen por primera vez. */
export function numerosDeVariables(body: string): number[] {
  const vistos: number[] = [];
  for (const m of body.matchAll(VARIABLE)) {
    const n = parseInt(m[1], 10);
    if (!vistos.includes(n)) vistos.push(n);
  }
  return vistos;
}

export function palabrasFijas(body: string): number {
  return body
    .replace(VARIABLE, " ")
    .split(/\s+/)
    .filter((p) => /[\p{L}\p{N}]/u.test(p)).length;
}

/** Las variables de un texto con un rótulo genérico, para completar el form cuando aparece una nueva. */
export function variablesPorDefecto(body: string, previas: TemplateVariable[] = []): TemplateVariable[] {
  return numerosDeVariables(body).map(
    (n) => previas.find((v) => v.n === n) ?? { n, label: `Variable ${n}`, source: "manual" as const },
  );
}

// ── el tipo de dato de una variable (#519) ─────────────────────────────────────────

/**
 * Qué dato va en la variable. Reemplaza al par «Qué dato es» + «Quién lo completa»: quién lo completa
 * se deduce del tipo.
 */
export type TipoDeDato =
  | "contact_name"
  | "operator_name"
  | "store_name"
  | "seller_name"
  | "quote_number"
  | "order_number"
  | "delivery_address"
  | "date"
  | "otro";

/**
 * Los tipos con nombre. Mientras el contrato del servicio sólo acepte `contact_name` y `manual`
 * (xcons-docs#520), los demás se guardan como `manual` con su `rotulo` fijo, y se reconocen por ese
 * texto: cambiar un rótulo acá deja de reconocer las plantillas ya guardadas con el anterior.
 */
export const TIPOS_DE_DATO: Record<Exclude<TipoDeDato, "otro">, { rotulo: string; solo: boolean; ejemplo: string }> = {
  contact_name: { rotulo: "Nombre del contacto", solo: true, ejemplo: "Carla" },
  operator_name: { rotulo: "Nombre del operador", solo: true, ejemplo: "Martín Gómez" },
  store_name: { rotulo: "Nombre del Store", solo: true, ejemplo: "XCONS" },
  seller_name: { rotulo: "Nombre del Seller", solo: true, ejemplo: "Corralón Norte" },
  quote_number: { rotulo: "Número de presupuesto", solo: false, ejemplo: "97234" },
  order_number: { rotulo: "Número de pedido", solo: false, ejemplo: "000689744" },
  delivery_address: { rotulo: "Dirección de entrega", solo: false, ejemplo: "Av. Cabildo 1234, CABA" },
  date: { rotulo: "Fecha", solo: false, ejemplo: "15/10/2026" },
};

const normalizar = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

/** El tipo de una variable guardada: por su `source`, o por su rótulo si es uno de los fijos. */
export function tipoDeDato(v: TemplateVariable): TipoDeDato {
  if (v.source === "contact_name") return "contact_name";
  const rotulo = normalizar(v.label);
  const tipo = (Object.keys(TIPOS_DE_DATO) as Exclude<TipoDeDato, "otro">[]).find(
    (t) => normalizar(TIPOS_DE_DATO[t].rotulo) === rotulo,
  );
  return tipo ?? "otro";
}

/** La variable que se guarda para un tipo. «Otro» lleva el nombre que escribió quien arma la plantilla. */
export function variableDelTipo(n: number, tipo: TipoDeDato, nombreDeOtro = ""): TemplateVariable {
  if (tipo === "otro") return { n, label: nombreDeOtro, source: "manual" };
  return { n, label: TIPOS_DE_DATO[tipo].rotulo, source: tipo === "contact_name" ? "contact_name" : "manual" };
}

/** Los motivos por los que Meta rechazaría el texto. Vacío si pasa. Espejo del conversation-service. */
export function validarTemplate(t: TextoDeTemplate, opts: { sinNombre?: boolean } = {}): string[] {
  const motivos: string[] = [];
  if (!opts.sinNombre) {
    const name = (t.name ?? "").trim();
    if (!name) motivos.push("Falta el nombre");
    else if (!NOMBRE.test(name)) motivos.push("El nombre sólo admite minúsculas, números y guión bajo");
    else if (name.length > LARGO_MAXIMO_DEL_NOMBRE) motivos.push(`El nombre tiene más de ${LARGO_MAXIMO_DEL_NOMBRE} caracteres`);
  }
  const body = (t.body ?? "").trim();
  if (!body) {
    motivos.push("Falta el texto");
    return motivos;
  }
  if (body.length > LARGO_MAXIMO_DEL_BODY) motivos.push(`El texto tiene más de ${LARGO_MAXIMO_DEL_BODY} caracteres`);
  if (/\t/.test(body) || / {4,}/.test(body)) motivos.push("El texto no admite tabulaciones ni más de tres espacios seguidos");
  if (/\n{3,}/.test(body)) motivos.push("El texto no admite más de dos saltos de línea seguidos");
  const sinTokens = body.replace(VARIABLE, "").split(MARCADOR_DE_TIENDA).join("");
  if (/[{}]/.test(sinTokens)) motivos.push("Las llaves de una variable están mal: usá {{1}}, {{2}}…");
  const numeros = numerosDeVariables(body);
  if (!numeros.every((n, i) => n === i + 1)) motivos.push("Las variables tienen que ir en orden: {{1}}, {{2}}…");
  if (/^\{\{\d+\}\}/.test(body)) motivos.push("El texto no puede empezar con una variable");
  if (/\{\{\d+\}\}[\s.,;:!?¡¿]*$/.test(body)) motivos.push("El texto no puede terminar con una variable");
  if (numeros.length && palabrasFijas(body) < PALABRAS_FIJAS_POR_VARIABLE * numeros.length) {
    motivos.push(`Hay muchas variables para tan poco texto fijo: Meta pide al menos ${PALABRAS_FIJAS_POR_VARIABLE} palabras por variable`);
  }
  const examples = Array.isArray(t.examples) ? t.examples : [];
  if (examples.length !== numeros.length) motivos.push("Falta un ejemplo por variable");
  else {
    if (examples.some((e) => !String(e ?? "").trim())) motivos.push("Un ejemplo está vacío");
    if (examples.some((e) => /[{}]/.test(String(e)))) motivos.push("Un ejemplo no puede tener variables");
  }
  const footer = (t.footer ?? "").trim();
  if (footer.length > LARGO_MAXIMO_DEL_FOOTER) motivos.push(`El pie tiene más de ${LARGO_MAXIMO_DEL_FOOTER} caracteres`);
  if (/[{}]/.test(footer)) motivos.push("El pie no admite variables");
  if (t.variables) {
    const coinciden =
      t.variables.length === numeros.length &&
      t.variables.every((v, i) => v && v.n === i + 1 && !!v.label.trim() && (v.source === "contact_name" || v.source === "manual"));
    if (!coinciden) motivos.push("Las variables declaradas no coinciden con el texto");
  }
  return motivos;
}

export const renderizarTienda = (texto: string, tienda: string): string => texto.split(MARCADOR_DE_TIENDA).join(tienda.trim());

/** Las variables por sus valores: el preview del alta con los ejemplos, y el del envío con lo tipeado. */
export const renderizar = (body: string, params: string[]): string =>
  body.replace(VARIABLE, (_, n: string) => params[parseInt(n, 10) - 1] || `{{${n}}}`);

// ── cómo se nombra lo que dice Meta ───────────────────────────────────────────────

export type Tono = "ok" | "espera" | "problema" | "neutro";

export const ESTADOS: Record<TemplateStatus, { texto: string; tono: Tono }> = {
  pending: { texto: "Por publicar", tono: "espera" },
  in_review: { texto: "En revisión", tono: "espera" },
  approved: { texto: "Aprobado", tono: "ok" },
  rejected: { texto: "Rechazado", tono: "problema" },
  paused: { texto: "Pausado por Meta", tono: "problema" },
  disabled: { texto: "Deshabilitado por Meta", tono: "problema" },
  deleted: { texto: "Retirado", tono: "neutro" },
  error: { texto: "No se pudo publicar", tono: "problema" },
};

/** La categoría, con la leyenda fija (D19): no hay precio, hay qué significa cada una. */
export const CATEGORIAS: Record<string, { texto: string; aviso: string | null }> = {
  UTILITY: { texto: "Utility", aviso: null },
  MARKETING: {
    texto: "Marketing",
    aviso: "La categoría más cara: Meta la asigna cuando ve intención promocional. Cada envío se cobra como marketing.",
  },
  AUTHENTICATION: { texto: "Autenticación", aviso: null },
};

export const categoriaDe = (category: string | null | undefined) =>
  category ? (CATEGORIAS[category] ?? { texto: category, aviso: null }) : null;

/** Lo que la ficha tiene que avisar de una copia: aprobada como marketing, o un cambio anunciado. */
export function avisoDeCategoria(pub: Pick<TemplatePublication, "status" | "category" | "reason"> | null): string | null {
  if (!pub) return null;
  if (pub.status === "approved" && pub.category === "MARKETING") return CATEGORIAS.MARKETING.aviso;
  if (pub.reason?.startsWith("Meta la va a cambiar")) return pub.reason;
  return null;
}

/** El estado de la copia de este canal, o por qué no hay copia. */
export function estadoDe(item: TemplateItem, conectado: boolean): { texto: string; tono: Tono } {
  // Un estado que el servicio no normalizó se muestra tal cual, en vez de romper la ficha.
  if (item.publication) return ESTADOS[item.publication.status] ?? { texto: String(item.publication.status), tono: "neutro" };
  return conectado ? { texto: "Sin publicar en este canal", tono: "neutro" } : { texto: "Se publica al conectar el número", tono: "neutro" };
}

// ── para enviar ───────────────────────────────────────────────────────────────────

export interface TemplateOfrecible {
  publication: TemplatePublication;
  definition: TemplateDefinition;
}

/**
 * Lo que el selector ofrece (D15): las copias aprobadas de este canal, y de cada nombre sólo la
 * versión aprobada más nueva. Una versión nueva en revisión no se ofrece todavía. La versión de la
 * tienda de una plantilla del catálogo tapa a la del catálogo, sea cual sea el número.
 */
export function ofrecibles(items: TemplateItem[]): TemplateOfrecible[] {
  const porNombre = new Map<string, TemplateOfrecible>();
  for (const item of items) {
    const pub = item.publication;
    if (!pub || pub.status !== "approved" || pub.deletedAt) continue;
    const actual = porNombre.get(item.definition.name);
    const gana = !actual || (actual.definition.catalog && !item.definition.catalog)
      ? true
      : actual.definition.catalog === item.definition.catalog && item.definition.version > actual.definition.version;
    if (gana) porNombre.set(item.definition.name, { publication: pub, definition: item.definition });
  }
  return [...porNombre.values()].sort((a, b) => a.definition.name.localeCompare(b.definition.name));
}

/** Parece un teléfono, no un nombre: no se precarga en «Hola {{1}}». */
const pareceTelefono = (s: string) => /^[+\d\s()-]{6,}$/.test(s.trim());

/**
 * Los valores iniciales de las variables: los que se completan solos (el contacto, el operador, el
 * Store, el Seller), y vacío donde elige o tipea el operador.
 */
export function parametrosIniciales(
  def: Pick<TemplateDefinition, "variables" | "body">,
  contactName: string | null | undefined,
  datos: { operador?: string | null; store?: string | null; seller?: string | null } = {},
): string[] {
  const variables = def.variables.length ? def.variables : variablesPorDefecto(def.body);
  const nombre = contactName && !pareceTelefono(contactName) ? contactName.trim().split(/\s+/)[0] : "";
  return variables.map((v) => {
    switch (tipoDeDato(v)) {
      case "contact_name":
        return nombre;
      case "operator_name":
        return datos.operador?.trim() ?? "";
      case "store_name":
        return datos.store?.trim() ?? "";
      case "seller_name":
        return datos.seller?.trim() ?? "";
      default:
        return "";
    }
  });
}

export const parametrosCompletos = (params: string[]): boolean => params.every((p) => p.trim().length > 0);

/** Un evento `channel:templates` sobre la vista: la copia nueva reemplaza a la de su definición. */
export function aplicarPublicacion(view: TemplatesView, publication: TemplatePublication): TemplatesView {
  if (publication.channelId !== view.channelId) return view;
  let cambio = false;
  const items = view.items.map((item) => {
    if (item.definition.definitionId !== publication.definitionId) return item;
    cambio = true;
    return { ...item, publication: publication.deletedAt ? null : publication };
  });
  return cambio ? { ...view, items } : view;
}

/** Un mensaje que salió por un template. */
export const esDeTemplate = (payload: { template?: unknown } | undefined): boolean => Boolean(payload?.template);
